import React, { useState, useEffect, useRef } from "react";

import { add, format, parse } from "date-fns";
import { FETCH_EMPLOYEES } from "EmployerApplication/Actions";
import { useGetCurrentCompanyApplicationQuery, useGetPendingItemsQuery } from "EmployerApplication/api";
import ContactRequiredCard from "EmployerApplication/Components/ContactRequiredCard";
import DentalMinimum from "EmployerApplication/Components/DentalMinimum";
import { DocumentCardsSection } from "EmployerApplication/Components/DocumentCardsSection";
import {
  documentUpload,
  wageDocumentUpload,
  validateEmployerApplicationDocument as validateEmployerApplicationDocumentAction,
} from "EmployerApplication/Components/documentPicker/utils";
import ExpandingApplicationStepCardV2 from "EmployerApplication/Components/ExpandingApplicationStepCardV2";
import MissedDeadline from "EmployerApplication/Components/MissedDeadline";
import OutOfStateIssue from "EmployerApplication/Components/OutOfStateIssue";
import OwnerEnrollment from "EmployerApplication/Components/OwnerEnrollment";
import { getTitleCopy } from "EmployerApplication/Components/OwnerEnrollmentUtils";
import ReviewAndEditEmployeeTable from "EmployerApplication/Components/ReviewAndEditEmployeeTable";
import WageReportIssues from "EmployerApplication/Components/WageReportIssues";
import { showWageDocumentPicker as getShowWageDocumentPicker, getEmployees } from "EmployerApplication/Selectors";
import { useDispatch, connect } from "react-redux";
import { deserializeDate } from "Redux/serializers";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { BasicLoading } from "ZeroRuby";

import { BodyMedium } from "Cobalt/BodyText";
import { Surface, SurfaceSection, SurfaceHeader } from "Theme/Surface";

const StatusHeader = ({ status }) => {
  switch (status) {
    case "complete":
      return "Congratulations! Your Application is Complete!";
    case "completeContactRequired":
      return "More Information Requested";
    case "submitted":
      return "Application Submitted";
    case "submittedWithContactRequired":
      return "Application Submitted - Additional Action Required";
    case "incomplete":
      return "Your Application is Incomplete";
  }
};

const IncompleteMessage = ({ coverageStartDate, carrierDeadline }) => (
  <BodyMedium>
    In order to start your insurance on {coverageStartDate && format(coverageStartDate, "LLLL do")}, please complete these open items by{" "}
    {carrierDeadline && format(carrierDeadline, "LLLL do")}.
  </BodyMedium>
);

const CompletedMessage = ({ coverageStartDate }) => (
  <>
    <BodyMedium>
      You have completed your application! We will review your application and submit it within the next 1-2 business days. Your insurance is scheduled to start{" "}
      {coverageStartDate && format(coverageStartDate, "LLLL do")}.
    </BodyMedium>
    <br />
    <BodyMedium weight="bold">
      We may request additional information in order to submit your application. Please be on the lookout for communication from our team in case additional
      information is required.
    </BodyMedium>
  </>
);

const CompletedWithContactRequiredMessage = () => (
  <BodyMedium>Based on our review of your group, we require additional information before moving forward.</BodyMedium>
);

const SubmittedMessage = ({ coverageStartDate, submittedAt }) => (
  <>
    <BodyMedium>
      We have submitted your application to the insurance carrier! Your insurance is scheduled to start{" "}
      {coverageStartDate && format(coverageStartDate, "LLLL do")}. We will inform you as soon as we receive approval from the insurance carrier. We expect to
      hear confirmation between {submittedAt && format(add(submittedAt, { days: 7 }), "LLLL do")} and{" "}
      {submittedAt && format(add(submittedAt, { days: 14 }), "LLLL do")}.
    </BodyMedium>
    <br />
    <BodyMedium weight="bold">
      The carrier may request additional information in order to enroll your group. Please be on the lookout for communication from our team in case additional
      information is required.
    </BodyMedium>
  </>
);

const SubmittedWithContactRequiredMessage = ({ coverageStartDate }) => (
  <BodyMedium weight="bold">
    The carrier has requested additional information in order to enroll your group. Your insurance is still scheduled to start{" "}
    {coverageStartDate && format(coverageStartDate, "LLLL do")}.
  </BodyMedium>
);

const employeeApplicationTally = (employees) => {
  let completeApplications = 0;
  let eligibleEmployees = 0;
  employees.forEach((employee) => {
    if (employee.status === "Done" && employee.is_eligible) {
      completeApplications += 1;
    }
    if (employee.is_eligible) {
      eligibleEmployees += 1;
    }
  });

  return {
    incomplete: eligibleEmployees - completeApplications,
    complete: completeApplications,
  };
};

const documentUploadTally = (uploadDocsProps) => {
  const completeUploads = uploadDocsProps.filter((doc) => doc.uploadComplete && doc.validated);

  return {
    incomplete: uploadDocsProps.length - completeUploads.length,
    complete: completeUploads.length,
  };
};

const getWageReportIssuesTally = (wageReportIssues) => {
  const cardTally = {
    complete: 0,
    incomplete: 0,
  };

  // eslint-disable-next-line no-plusplus
  wageReportIssues.forEach(({ is_resolved }) => (is_resolved ? cardTally.complete++ : cardTally.incomplete++));

  return cardTally;
};

/** Update this function to add new pending item type */
function buildPendingItemsSections({ pendingItems, allowsDecliningEmployees, dentalCarrierName, scrollToEmployeeApplications, wageReportIssuesTally }) {
  // If there's a wage-report pending item, its sub-payload is the array of issues
  // We can determine it is "complete" by seeing if all wage issues are resolved
  const wageIsComplete = wageReportIssuesTally ? wageReportIssuesTally.incomplete === 0 : true;

  const sections = [];

  // coverage_start_date => MissedDeadline
  const missedDeadline = pendingItems.find((item) => item.type === "coverage_start_date");
  if (missedDeadline) {
    sections.push({
      type: "coverage_start_date",
      title: "Update Coverage Start Date",
      component: <MissedDeadline />,
      isComplete: false,
    });
  }

  // owner => OwnerEnrollment
  const ownerItem = pendingItems.find((item) => item.type === "owner");
  if (ownerItem) {
    const ownerIssueType = ownerItem.payload.owner_issue_type;
    sections.push({
      type: "owner",
      title: getTitleCopy(ownerIssueType, allowsDecliningEmployees),
      component: <OwnerEnrollment pendingItemPayload={ownerItem.payload} scrollToEmployeeApplications={scrollToEmployeeApplications} />,
      isComplete: false,
    });
  }

  // dental_minimum => DentalMinimum
  const dentalItem = pendingItems.find((item) => item.type === "dental_minimum");
  if (dentalItem) {
    sections.push({
      type: "dental_minimum",
      title: "Confirm Your Dental Selection",
      component: (
        <DentalMinimum
          dentalCarrierName={dentalCarrierName}
          pendingItemPayload={dentalItem.payload}
          scrollToEmployeeApplications={scrollToEmployeeApplications}
        />
      ),
      isComplete: false,
    });
  }

  // out_of_state_issue => OutOfStateIssue
  const outOfStateItem = pendingItems.find((item) => item.type === "out_of_state_issue");
  if (outOfStateItem) {
    sections.push({
      type: "out_of_state_issue",
      title: "Out Of State Employees",
      component: <OutOfStateIssue outOfStateEmployees={outOfStateItem.payload} />,
      isComplete: false,
    });
  }

  // wage_report_issue => WageReportIssues
  // We know it might hold multiple sub-issues inside "payload"
  const wageItem = pendingItems.find((item) => item.type === "wage_report_issue");
  if (wageItem) {
    sections.push({
      type: "wage_report_issue",
      title: "Resolve Employee Discrepencies",
      subtitle:
        "Your Application will not be approved without proof that all listed employees on your application are associated with your business. The following employee(s) require additional explanation.",
      component: <WageReportIssues />,
      isComplete: wageIsComplete,
    });
  }

  return sections;
}

const ApplicationStatusPageDumb = ({ employees, showWageDocumentPicker }) => {
  const { data: companyApplication, refetch: refetchCompanyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const onDocumentUpload = (form) => documentUpload(form, companyPolicyId);
  const onWageDocumentUpload = (form) => wageDocumentUpload(form, companyPolicyId);
  const validateEmployerApplicationDocument = (document) => validateEmployerApplicationDocumentAction(document, companyPolicyId);
  const { data: pendingItemsRaw, isLoading: isLoadingPendingItems } = useGetPendingItemsQuery(companyPolicyId, { skip: !companyPolicyId });
  const pendingItems = pendingItemsRaw?.pending_items;

  const dispatch = useDispatch();

  useEffect(() => {
    if (employees.length === 0 && !isLoading) {
      dispatch({ type: FETCH_EMPLOYEES, payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only one reminder card can be open at a time. If we try and open a card while the other one is open, automatically close it first.
  const [openCard, setOpenCard] = useState(null);
  const toggleOpenCard = (cardName) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "application_status_page",
      action: "open_pending_item_card",
    });
    setOpenCard(cardName);
  };

  const employeeApplicationRef = useRef(null);
  const scrollToEmployeeApplications = () => {
    toggleOpenCard("employeeApplications");
    employeeApplicationRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  let carrierDeadline;
  let submittedAt;
  let coverageStartDate;
  let supportRep;
  let displayCarrierDocumentsStep;
  let displayContactOurTeam;
  let displayedApplicationReminders;
  let uploadDocsProps;
  let employeeApplicationsCompleted;
  let uploadDocumentsCompleted;
  let carrierDocumentsCompleted;
  let wageReportIssues;
  let wageReportIssuesTally;
  let allStepsCompleted;
  let pendingItemsSections = [];
  let contactRequiredReason;

  if (!isLoading && !isLoadingPendingItems && companyApplication && pendingItems) {
    coverageStartDate = parse(companyApplication.coverageStartDateAt, "MM/dd/yyyy", new Date());
    carrierDeadline = parse(companyApplication.carrierDeadlines[companyApplication.coverageStartDateAt], "yyyy-MM-dd", new Date());
    submittedAt = deserializeDate(companyApplication.submittedAt);
    displayedApplicationReminders = companyApplication.displayedApplicationReminders;
    supportRep = displayedApplicationReminders.support_rep_contact_info;
    contactRequiredReason = displayedApplicationReminders?.contact_our_team_required_reason;

    // Documents
    uploadDocsProps = companyApplication.uploadDocsProps || [];
    uploadDocumentsCompleted = companyApplication.applicationStatus.sections.uploadDocuments && companyApplication.applicationStatus.sections.validDocuments;
    carrierDocumentsCompleted = uploadDocsProps.filter((doc) => !!doc.meta?.supplementaryRequirement).every((doc) => doc.meta?.uploadComplete && doc.validated);
    displayCarrierDocumentsStep = !carrierDocumentsCompleted; // This is correct, this component just has older patterns

    // Employees
    employeeApplicationsCompleted = companyApplication.applicationStatus.sections.employeeApplications;

    // "Contact our team"
    displayContactOurTeam = displayedApplicationReminders.contact_our_team_required;

    // If there's a wage_report_issue item, gather the sub-issues for tally
    const wageReportPendingItem = pendingItems.find((pi) => pi.type === "wage_report_issue");
    wageReportIssues = wageReportPendingItem ? wageReportPendingItem.payload.filter((issue) => issue.validation_issue_type === "missing_from_doc") : [];
    wageReportIssuesTally = getWageReportIssuesTally(wageReportIssues);

    // Build a brand-new array describing each "pending item" card to display
    pendingItemsSections = buildPendingItemsSections({
      pendingItems,
      allowsDecliningEmployees: companyApplication.allowsDecliningEmployees,
      dentalCarrierName: companyApplication.dentalPlanBrand,
      scrollToEmployeeApplications,
      wageReportIssuesTally,
    });

    // All pending items are done if we have zero, or if all are complete
    const pendingItemsCompleted = pendingItemsSections.length === 0 || pendingItemsSections.every((section) => section.isComplete);

    // Finally check all steps
    allStepsCompleted = employeeApplicationsCompleted && uploadDocumentsCompleted && carrierDocumentsCompleted && pendingItemsCompleted;
  }

  let status = "incomplete";
  let statusInformation;

  if (submittedAt) {
    if (displayContactOurTeam) {
      statusInformation = <SubmittedWithContactRequiredMessage coverageStartDate={coverageStartDate} />;
      status = "submittedWithContactRequired";
    } else {
      statusInformation = <SubmittedMessage coverageStartDate={coverageStartDate} submittedAt={submittedAt} />;
      status = "submitted";
    }
  } else if (allStepsCompleted) {
    if (displayContactOurTeam) {
      statusInformation = <CompletedWithContactRequiredMessage />;
      status = "completeContactRequired";
    } else {
      statusInformation = <CompletedMessage coverageStartDate={coverageStartDate} />;
      status = "complete";
    }
  } else {
    statusInformation = <IncompleteMessage coverageStartDate={coverageStartDate} carrierDeadline={carrierDeadline} />;
  }

  // On load, automatically open the first incomplete card
  useEffect(() => {
    // The wage report card syncs data on form change, which updates the component
    // props and triggers this side effect. To prevent other cards from opening,
    // skip this step if the wage report card is currently open.
    if (openCard || isLoading || isLoadingPendingItems || submittedAt) return;

    // If employees are done, but docs incomplete: open said docs
    if (!uploadDocumentsCompleted) {
      setOpenCard("uploadDocuments");
      return;
    }

    if (displayCarrierDocumentsStep && !carrierDocumentsCompleted) {
      setOpenCard("carrierDocuments");
      return;
    }

    // Try to open the first incomplete pending item, if any
    const firstIncompletePendingItem = pendingItemsSections.find((section) => !section.isComplete);
    if (firstIncompletePendingItem) {
      setOpenCard(firstIncompletePendingItem.type);
      return;
    }

    // If no incomplete pending items, but employees incomplete => open employees
    if (!employeeApplicationsCompleted) {
      setOpenCard("employeeApplications");
    }
  }, [
    openCard,
    pendingItemsSections,
    submittedAt,
    employeeApplicationsCompleted,
    displayCarrierDocumentsStep,
    uploadDocumentsCompleted,
    isLoading,
    isLoadingPendingItems,
    carrierDocumentsCompleted,
  ]);

  if (isLoading || isLoadingPendingItems) {
    return <BasicLoading />;
  }

  /** Required submissions are the initial requirements based on the core attributes of this group, e.g. size, date established > the initial carrier/wage requirements */
  const requiredSubmissions = uploadDocsProps.filter((doc) => !doc.meta?.supplementaryRequirement);
  /** Supplementary submissions: dynamically-generated additional requirements, which could be based on other submissions, edge cases, etc */
  const supplementarySubmissions = uploadDocsProps.filter((doc) => !!doc.meta?.supplementaryRequirement && !doc.originalFilename?.includes("autogenerated"));

  return (
    <Surface mb={4}>
      <SurfaceHeader>
        <StatusHeader status={status} />
      </SurfaceHeader>

      <SurfaceSection>
        {statusInformation}

        {/* If "contact our team" is flagged, display this card */}
        {displayContactOurTeam && <ContactRequiredCard supportRep={supportRep} contactRequiredReason={contactRequiredReason} />}

        {/* Upload Documents */}
        {!submittedAt && (
          <ExpandingApplicationStepCardV2
            cardTitle="Upload Required Documents"
            cardSubtitle="We need the following documents before we can submit your application."
            cardTally={documentUploadTally(requiredSubmissions)}
            cardBody={
              <DocumentCardsSection
                uploadDocsProps={requiredSubmissions}
                showWageDocumentPicker={showWageDocumentPicker}
                onDocumentUpload={onDocumentUpload}
                onWageDocumentUpload={onWageDocumentUpload}
                validateEmployerApplicationDocument={validateEmployerApplicationDocument}
                onDocumentUploadSuccess={refetchCompanyApplication}
              />
            }
            isComplete={uploadDocumentsCompleted}
            isOpen={openCard === "uploadDocuments"}
            toggleOpenCard={() => toggleOpenCard(openCard === "uploadDocuments" ? "allClosed" : "uploadDocuments")}
          />
        )}

        {/* Supplementary Requirements (Additional Carrier Docs) */}
        {supplementarySubmissions.length > 0 && displayCarrierDocumentsStep && !submittedAt && (
          <ExpandingApplicationStepCardV2
            cardTitle="Additional Carrier Requirements"
            cardSubtitle="One or more of the carriers you are enrolling with require additional documentation."
            cardTally={documentUploadTally(supplementarySubmissions)}
            cardBody={
              <DocumentCardsSection
                uploadDocsProps={supplementarySubmissions}
                showWageDocumentPicker={showWageDocumentPicker}
                onDocumentUpload={onDocumentUpload}
                onWageDocumentUpload={onWageDocumentUpload}
                validateEmployerApplicationDocument={validateEmployerApplicationDocument}
                onDocumentUploadSuccess={refetchCompanyApplication}
                /**
                 * Because we are giving customers a hassle here
                 * we show them the reason why up-front
                 * it is rendered inline instead of in a modal
                 * @see https://simplyinsured.atlassian.net/browse/SIM2-128
                 */
                showWhyRequired
              />
            }
            isComplete={carrierDocumentsCompleted}
            isOpen={openCard === "carrierDocuments"}
            toggleOpenCard={() => toggleOpenCard(openCard === "carrierDocuments" ? "allClosed" : "carrierDocuments")}
          />
        )}

        {/* Display each "pending item" as its own card (if not submitted) */}
        {pendingItemsSections.map((section) => {
          if (submittedAt) return null; // hide after submission
          return (
            <ExpandingApplicationStepCardV2
              key={section.type}
              cardTitle={section.title}
              cardSubtitle={section.subtitle}
              cardTally={{
                complete: section.isComplete ? 1 : 0,
                incomplete: section.isComplete ? 0 : 1,
              }}
              cardBody={section.component}
              isComplete={section.isComplete}
              isOpen={openCard === section.type}
              toggleOpenCard={() => toggleOpenCard(openCard === section.type ? "allClosed" : section.type)}
            />
          );
        })}

        <div ref={employeeApplicationRef}>
          <ExpandingApplicationStepCardV2
            cardTitle="Complete Employee Applications"
            cardSubtitle="All eligible employees must choose to enroll or decline in coverage before we can submit your application."
            cardTally={employeeApplicationTally(employees)}
            cardBody={<ReviewAndEditEmployeeTable employees={employees} disableEdits />}
            isComplete={employeeApplicationsCompleted}
            isOpen={openCard === "employeeApplications"}
            toggleOpenCard={() => toggleOpenCard(openCard === "employeeApplications" ? "allClosed" : "employeeApplications")}
          />
        </div>
      </SurfaceSection>
    </Surface>
  );
};

const mapStateToProps = (state) => ({
  // props for employee applications card
  employees: getEmployees(state),
  showWageDocumentPicker: getShowWageDocumentPicker(state),
});

const withRedux = connect(mapStateToProps);
export default withRedux(ApplicationStatusPageDumb);
